import React from 'react';
import {
    LeaderboardDetail,
    TimeframeEnum,
    TimeframeDetail,
    TimeframeManualDetail,
    TimeframeDetailErrors,
    TimeframeManualDetailErrors,
} from '../interfaces/ILeaderboardConfig';
import { useFormikContext } from 'formik';

import { Divider, Box } from '@chakra-ui/react';

import CustomValidationControl from '../components/CustomValidationControl';
import { DateTimePickerChain } from '../components/DateTimePickerChain';
import DateScheduler, { ScheduleType } from '../components/DateScheduler';

import { getUnixDate, getDateFromUnix } from '../util/datetime';
import * as LBCONFIG from '../consts/LeaderboardConfig';

type TimeframeProps = {
    name: string;
    info: TimeframeDetail | TimeframeManualDetail;
    timeframe: TimeframeEnum;
    onChange: (selectedTimeframe: TimeframeDetail | TimeframeManualDetail) => void;
    onHelpClick?: any;
    [x: string]: any;
};

const TimeframeDetails: React.FC<TimeframeProps> = ({
    info,
    name,
    timeframe,
    onChange,
    onHelpClick,
    ...rest
}) => {
    const { values, touched, errors, setFieldValue, handleBlur, handleChange } =
        useFormikContext<LeaderboardDetail>();

    const onClickHelp = (title: string, content: string) => {
        onHelpClick(title, content);
    };
    // get the selected timeframe from the config
    const timeframeConfig = info;
    // check if the manual option was selected
    const isManual = timeframe === TimeframeEnum.MANUAL;
    // check if the the other options were selected
    const isTimeFrame =
        !isManual && typeof timeframeConfig === 'object' && 'rotation_trigger' in timeframeConfig;

    const timeframeSelected = !isManual
        ? (values.timeframes[timeframe] as TimeframeDetail)
        : undefined;

    const manualSelected = isManual
        ? (values.timeframes[timeframe] as TimeframeManualDetail)
        : undefined;

    const handleDateChange = (index: number, date: Date) => {
        const newSelectedDates = [...manualSelected!.start_dates];
        if (date === null) {
            newSelectedDates.splice(index, 1);
        } else {
            newSelectedDates[index] = getUnixDate(date);
        }
        setFieldValue(`${name}.start_dates`, newSelectedDates);
    };

    const rotationTrigger = isTimeFrame ? Number(timeframeSelected!.rotation_trigger) : 0;
    const displayDate = getDateFromUnix(rotationTrigger).toISOString();

    const getErrorDetail = (property: keyof TimeframeDetail) => {
        if (!property) return undefined;
        if (isManual) return undefined;
        const touchedTimeframeDetail = touched?.timeframes?.[timeframe]
            ? (touched.timeframes[timeframe] as unknown as TimeframeDetail)
            : undefined;

        const timeframeErrorObj = errors.timeframes?.[timeframe]
            ? (errors.timeframes[timeframe] as TimeframeDetailErrors)
            : undefined;

        return timeframeErrorObj?.[property] && touchedTimeframeDetail?.[property]
            ? timeframeErrorObj?.[property]
            : undefined;
    };

    const getErrorManual = (property: keyof TimeframeManualDetail) => {
        if (!property) return undefined;
        if (!isManual) return undefined;
        const touchedTimeframeManual = touched?.timeframes?.[timeframe]
            ? (touched?.timeframes?.[timeframe] as unknown as TimeframeManualDetail)
            : undefined;
        const timeframeErrorObj = errors.timeframes?.[timeframe]
            ? (errors.timeframes[timeframe] as TimeframeManualDetailErrors)
            : undefined;
        return touchedTimeframeManual?.[property] && timeframeErrorObj?.[property]
            ? timeframeErrorObj?.[property]
            : undefined;
    };

    const convertedStartDates = isManual
        ? manualSelected?.start_dates.map((dt: number) => {
              if (dt <= 0) return 0;
              const newDate = getDateFromUnix(dt);
              return newDate;
          })
        : [new Date()];

    return (
        <Box {...rest}>
            <Box>
                <CustomValidationControl
                    type="number"
                    name={`${name}.retention`}
                    value={timeframeSelected?.retention || manualSelected?.retention}
                    onChange={(num) => {
                        setFieldValue(`${name}.retention`, Number(num));
                    }}
                    onBlur={handleBlur}
                    label="Retention"
                    data-group={timeframe}
                    isRequired={true}
                    help={{
                        onClick: () => onClickHelp('Retention', LBCONFIG.HELP_TIMEFRAME_RETENTION),
                    }}
                    validationMessage={getErrorDetail('retention') || getErrorManual('retention')}
                ></CustomValidationControl>
                <CustomValidationControl
                    type="checkbox"
                    name={`${name}.archive`}
                    value={isManual ? manualSelected?.archive : timeframeSelected?.archive}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    label="Archive"
                    data-group={timeframe}
                    help={{
                        onClick: () => onClickHelp('Archive', LBCONFIG.HELP_TIMEFRAME_ARCHIVE),
                    }}
                    display="flex"
                ></CustomValidationControl>
            </Box>
            {isTimeFrame && (
                <DateScheduler
                    name={`${name}.rotation_trigger`}
                    type={timeframe as ScheduleType}
                    defaultValue={rotationTrigger > 0 ? new Date(displayDate) : undefined}
                    onDateChange={(dt) =>
                        setFieldValue(`${name}.rotation_trigger`, getUnixDate(dt))
                    }
                ></DateScheduler>
            )}
            {isManual && (
                <DateTimePickerChain
                    maxPickers={30}
                    onChange={handleDateChange}
                    title="Start Dates"
                    value={convertedStartDates}
                    name={`${name}.start_dates`}
                    help={{
                        onClick: () =>
                            onClickHelp('Start Dates', LBCONFIG.HELP_TIMEFRAME_START_DATES),
                    }}
                    validationMessage={getErrorManual('start_dates')}
                ></DateTimePickerChain>
            )}
            <Divider mt={3} mb={3} />
        </Box>
    );
};

export default TimeframeDetails;
